import {useCallback, useEffect, useState} from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {patientService} from '../../services/patientService';
import {analytics} from '../../services/analytics';
import TeDictationButton from '../telephone/TeDictationButton';
import StarRatingInput, {RATING_DESCRIPTIONS} from './StarRatingInput';

// ═══════════════════════════════════════════════════════════════
// SessionRatingModal
// ═══════════════════════════════════════════════════════════════
//
// Compact, opt-in rating dialog reached from the encounter-detail overflow
// menu ("Rating"). It is deliberately low-key:
//   • If the session was already rated, it opens in a succinct "view" state —
//     the stars they gave, a one-line thank-you, and a quiet "Change rating"
//     button — rather than nagging them to rate again.
//   • If it hasn't been rated, it opens straight into the editable stars +
//     optional written/dictated feedback.
//
// Persistence and accessibility mirror `SessionRatingCard` (same
// `patientService.rateScribeSession` write, same accessible `StarRatingInput`).

const ACCENT = '#117DB1';

interface SessionRatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId?: string | null;
  initialRating?: number | null;
  initialReason?: string | null;
  accentColor?: string;
  onRated?: (rating: number, reason: string) => void;
}

export default function SessionRatingModal({
  isOpen,
  onClose,
  conversationId,
  initialRating,
  initialReason,
  accentColor = ACCENT,
  onRated,
}: SessionRatingModalProps) {
  const toast = useToast();
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const fieldBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const quoteColor = useColorModeValue('gray.600', 'gray.300');

  const [rating, setRating] = useState<number>(initialRating || 0);
  const [reason, setReason] = useState<string>(initialReason || '');
  const [isEditing, setIsEditing] = useState<boolean>(!initialRating);
  const [isSaving, setIsSaving] = useState(false);

  // Re-seed each time the dialog opens so it reflects the latest saved rating
  // (and resets a half-finished edit from a previous open).
  useEffect(() => {
    if (!isOpen) return;
    setRating(initialRating || 0);
    setReason(initialReason || '');
    setIsEditing(!initialRating);
    setIsSaving(false);
  }, [isOpen, initialRating, initialReason]);

  const handleDictation = useCallback((transcript: string) => {
    const clean = transcript.trim();
    if (!clean) return;
    setReason(prev => (prev.trim() ? `${prev.trim()} ${clean}` : clean));
  }, []);

  const handleSave = useCallback(async () => {
    if (!conversationId || rating <= 0 || isSaving) return;
    setIsSaving(true);
    const trimmed = reason.trim();
    try {
      await patientService.rateScribeSession(conversationId, {
        rating,
        reason: trimmed,
      });
      try {
        analytics.capture('session_rating_submitted', {
          conversation_id: conversationId,
          rating,
          had_reason: Boolean(trimmed),
          reason_length: trimmed.length,
          source: 'encounter_detail',
        });
      } catch (_) {
        /* telemetry is best-effort */
      }
      onRated?.(rating, trimmed);
      setReason(trimmed);
      setIsEditing(false);
      toast({
        title: 'Thanks for the feedback',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: 'Could not save rating',
        description: err?.message || 'Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [conversationId, rating, reason, isSaving, onRated, toast]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius="xl" mx={4}>
        <ModalHeader fontSize="md" fontWeight="700">
          {isEditing ? 'Rate this session' : 'Your rating'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isEditing ? (
            <VStack align="stretch" spacing={4}>
              <Flex align="center" gap={2} wrap="wrap">
                <StarRatingInput
                  value={rating}
                  onChange={setRating}
                  accentColor={accentColor}
                />
                {rating > 0 ? (
                  <Text color={textColor} fontWeight="600" fontSize="sm" ml={1}>
                    {RATING_DESCRIPTIONS[rating]}
                  </Text>
                ) : null}
              </Flex>

              <VStack align="stretch" spacing={2}>
                <Flex
                  align="center"
                  justify="space-between"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Text
                    as="label"
                    htmlFor="session-rating-feedback"
                    color={subtleText}
                    fontSize="sm"
                  >
                    Any feedback? (optional)
                  </Text>
                  <TeDictationButton
                    onInsert={handleDictation}
                    isDisabled={isSaving}
                    label="Dictate"
                  />
                </Flex>
                <Textarea
                  id="session-rating-feedback"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder={
                    rating > 0 && rating <= 3
                      ? 'What could be better?'
                      : 'What went well?'
                  }
                  isDisabled={isSaving}
                  bg={fieldBg}
                  borderColor={borderColor}
                  borderRadius="lg"
                  minH="88px"
                  fontSize="sm"
                  resize="vertical"
                  _focusVisible={{
                    borderColor: accentColor,
                    boxShadow: `0 0 0 1px ${accentColor}`,
                  }}
                />
              </VStack>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={3}>
              <StarRatingInput value={rating} onChange={() => {}} readOnly />
              <Text color={textColor} fontSize="sm">
                You rated this session{' '}
                <Text as="span" fontWeight="700">
                  {rating}/5
                </Text>{' '}
                — {RATING_DESCRIPTIONS[rating]}. Thanks for your feedback!
              </Text>
              {reason ? (
                <Text
                  color={quoteColor}
                  fontSize="sm"
                  fontStyle="italic"
                  borderLeftWidth="2px"
                  borderColor={borderColor}
                  pl={3}
                >
                  “{reason}”
                </Text>
              ) : null}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {isEditing ? (
            <>
              <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
                Cancel
              </Button>
              <Button
                bg={accentColor}
                color="white"
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={rating <= 0}
                _hover={{bg: '#0E6B98'}}
                _active={{bg: '#0C5C82'}}
              >
                Save rating
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                Change rating
              </Button>
              <Button
                bg={accentColor}
                color="white"
                onClick={onClose}
                _hover={{bg: '#0E6B98'}}
                _active={{bg: '#0C5C82'}}
              >
                Done
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
